import React, { useCallback } from 'react';
import type Transit from '@this/domain/transit/transit';
import type TransitList from '@this/domain/transit/transit_list';
import type SelectRepository from '@this/domain/select_repository';
import type SelectStore from '@this/domain/select_store';
import type Hotel from '@this/domain/hotel/hotel';
import type SearchResult from '@this/domain/search_result';
import type SearchQueryItem from '@this/src/domain/search_query_item';
import { reportError } from '@this/lib/bugsnag';
import { getFirstFromArray } from '@this/src/util';
import TransportBoxShortDistance from '../../transport_box/transport_box_short_distance';

interface TransportListShortDistanceProps {
  queryItem?: SearchQueryItem;
  onChange: () => void;
  transits: TransitList;
  repository: SelectRepository;
  store: SelectStore;
  direction?: string;
  parentLoading?: (loading: boolean) => void;
}

const TransportListShortDistance = ({
  queryItem,
  onChange,
  transits,
  repository,
  store,
  direction,
  parentLoading
}: TransportListShortDistanceProps) => {
  //　Question: 0だと往路、2だと復路？PackageItemsのjsonデータから判断つく情報ある？
  const fetchSelectedFlightNames = useCallback(
    (num: number): string | undefined => {
      const packageItems = store.result.packageItems;
      const airline = store.result.airline;

      const item = packageItems?.[airline]?.[num];
      if (!item) return undefined;

      const current = item.currentElement();
      if (!current) return undefined;

      const transit = current as Transit;
      const firstSegment = getFirstFromArray(transit.segments, 'fetchSelectedFlightNames: transit segments');
      if (!firstSegment?.legs) return undefined;

      return firstSegment.legs.map(leg => leg.name).join(',') || undefined;
    },
    [store.result]
  );

  // TODO:旧コメント changeable パラメータの用途を明確化する必要がある
  // MEMO: changeable パラメータは引数としては現在未使用（常にundefined）だが、app.render()とselectedPriceOriginalIndex設定のためselectWithAnimationの呼び出しは必要っぽい
  const handleSelect = useCallback(
    async (
      t: Transit,
      opts: {
        changeable?: boolean;
        domesticAirPriceIndex?: number;
      } = {}
    ) => {
      if (!transits.canSelectSameDay(t.id, opts.domesticAirPriceIndex)) return;

      const searchResult: SearchResult = store.result;

      if (searchResult.type === 'airPackage') {
        const segment = getFirstFromArray(
          t.segments,
          'TransportListShortDistance: transit segments for air package'
        );
        if (segment == null) {
          onChange();
          transits.selectWithAnimation(t.id, opts.changeable, opts.domesticAirPriceIndex);
          return;
        }

        switch (searchResult.packageProvider) {
          case 'rakuten': {
            const hotel: Hotel | null = searchResult.getRakutenPackageCurrentHotel();
            const outFlightName =
              direction === 'out' ? segment.legs.map(l => l.name).join(',') : fetchSelectedFlightNames(0);
            const homeFlightName =
              direction === 'home' ? segment.legs.map(l => l.name).join(',') : fetchSelectedFlightNames(2);

            await repository.fetchRakutenPackages({
              outFlightName,
              homeFlightName,
              airlines: [searchResult.airline],
              hotelName: hotel ? hotel.name : null,
              airCode: searchResult.getRakutenPackageAirCode()
            });
            break;
          }
          default:
            break;
        }
      }
      onChange();
      transits.selectWithAnimation(t.id, opts.changeable, opts.domesticAirPriceIndex);
    },
    [direction, fetchSelectedFlightNames, onChange, repository, store.result, transits]
  );

  try {
    return (
      <div className="select-transport-list">
        {transits.listBySegment().map(transit => (
          <TransportBoxShortDistance
            key={transit.id}
            queryItem={queryItem}
            handleClickTab={store.handleClickTab}
            handleSelect={handleSelect}
            transit={transit}
            inList
            selected={transits.currentId === transit.id}
            domesticAirPriceIndex={transits.domesticAirPriceIndex || 0}
            tab={undefined}
            store={store}
            parentLoading={parentLoading}
          />
        ))}
      </div>
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

export default TransportListShortDistance;
